import type { IPointOfSale } from '../shared/models/core/point-of-sale';
import type { IProduct } from '../shared/models/sale/product';
import type { CreateSaleMovementRequest } from '../shared/models/sale/create-sale-movement-request';
import { MaterialCategory } from '../shared/models/sale/material-category.enum';
import { Constants } from '../shared/models/sale/constants';
import { filterOfflineProducts, type OfflineProductFilterOptions } from './productFilter';
import type {
  OfflineCacheRecord,
  OfflineCorrelative,
  OfflineProductRecord,
  OfflineProductStageRecord,
  OfflineReadiness,
  OfflineSaleRecord,
  OfflineSaleStatus,
} from './types';

const DB_NAME = 'ePosOfflineV2';
const DB_VERSION = 2;
const STORE_PRODUCTS = 'products';
const STORE_CACHE = 'cache';
const STORE_CORRELATIVES = 'correlatives';
const STORE_OUTBOX = 'saleOutbox';
const STORE_PRODUCT_STAGE = 'productSyncStage';

let dbPromise: Promise<IDBDatabase> | undefined;

const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });

const transactionDone = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });

export const openOfflineDb = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
        const store = db.createObjectStore(STORE_PRODUCTS, { keyPath: '_offlineKey' });
        store.createIndex('companyPos', ['_companyTaxId', '_pointOfSaleId'], { unique: false });
        store.createIndex('companyPosCode', ['_companyTaxId', '_pointOfSaleId', 'code'], {
          unique: false,
        });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_CORRELATIVES)) {
        const store = db.createObjectStore(STORE_CORRELATIVES, { keyPath: 'id' });
        store.createIndex('supplier', 'supplierId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        const store = db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('companyPos', ['companyTaxId', 'pointOfSaleId'], { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_PRODUCT_STAGE)) {
        const store = db.createObjectStore(STORE_PRODUCT_STAGE, { keyPath: '_offlineKey' });
        store.createIndex('companyPos', ['_companyTaxId', '_pointOfSaleId'], { unique: false });
        store.createIndex('companyPosSync', ['_companyTaxId', '_pointOfSaleId', '_syncId'], {
          unique: false,
        });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open offline database'));
  });

  return dbPromise;
};

export const buildOfflineProductKey = (
  product: Pick<IProduct, 'code' | 'vin' | 'marketCode' | 'warehouseCode'>,
  pointOfSaleId: string
) =>
  [
    pointOfSaleId,
    product.vin?.trim() || product.code?.trim(),
    product.marketCode?.trim(),
    product.warehouseCode?.trim(),
  ].join('|');

export const cacheProducts = async (
  products: IProduct[],
  companyTaxId: string,
  pointOfSaleId: string
): Promise<void> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_PRODUCTS, 'readwrite');
  const store = tx.objectStore(STORE_PRODUCTS);
  const cachedAt = new Date().toISOString();

  for (const product of products) {
    const row: OfflineProductRecord = {
      ...product,
      _offlineKey: buildOfflineProductKey(product, pointOfSaleId),
      _companyTaxId: companyTaxId,
      _pointOfSaleId: pointOfSaleId,
      _cachedAt: cachedAt,
    };
    store.put(row);
  }
  await transactionDone(tx);
};

export const replaceProducts = async (
  products: IProduct[],
  companyTaxId: string,
  pointOfSaleId: string
): Promise<void> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_PRODUCTS, 'readwrite');
  const store = tx.objectStore(STORE_PRODUCTS);
  const index = store.index('companyPos');
  const existing = await requestToPromise(index.getAllKeys([companyTaxId, pointOfSaleId]));
  existing.forEach((key) => store.delete(key));

  const cachedAt = new Date().toISOString();
  products.forEach((product) => {
    const row: OfflineProductRecord = {
      ...product,
      _offlineKey: buildOfflineProductKey(product, pointOfSaleId),
      _companyTaxId: companyTaxId,
      _pointOfSaleId: pointOfSaleId,
      _cachedAt: cachedAt,
    };
    store.put(row);
  });
  await transactionDone(tx);
};

export const resetProductSyncStage = async (
  companyTaxId: string,
  pointOfSaleId: string
): Promise<void> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_PRODUCT_STAGE, 'readwrite');
  const store = tx.objectStore(STORE_PRODUCT_STAGE);
  const keys = await requestToPromise(
    store.index('companyPos').getAllKeys([companyTaxId, pointOfSaleId])
  );
  keys.forEach((key) => store.delete(key));
  await transactionDone(tx);
};

export const stageProductBatch = async (
  products: IProduct[],
  companyTaxId: string,
  pointOfSaleId: string,
  syncId: string
): Promise<void> => {
  if (!products.length) return;

  const db = await openOfflineDb();
  const tx = db.transaction(STORE_PRODUCT_STAGE, 'readwrite');
  const store = tx.objectStore(STORE_PRODUCT_STAGE);
  const cachedAt = new Date().toISOString();

  products.forEach((product) => {
    const row: OfflineProductStageRecord = {
      ...product,
      _offlineKey: buildOfflineProductKey(product, pointOfSaleId),
      _companyTaxId: companyTaxId,
      _pointOfSaleId: pointOfSaleId,
      _cachedAt: cachedAt,
      _syncId: syncId,
    };
    store.put(row);
  });

  await transactionDone(tx);
};

export const commitProductSyncStage = async (
  companyTaxId: string,
  pointOfSaleId: string,
  syncId: string
): Promise<number> => {
  const db = await openOfflineDb();
  const readTx = db.transaction(STORE_PRODUCT_STAGE, 'readonly');
  const staged = (await requestToPromise(
    readTx
      .objectStore(STORE_PRODUCT_STAGE)
      .index('companyPosSync')
      .getAll([companyTaxId, pointOfSaleId, syncId])
  )) as OfflineProductStageRecord[];
  await transactionDone(readTx);

  if (!staged.length) {
    throw new Error(
      'La sincronizacion de productos no devolvio registros para el PV seleccionado.'
    );
  }

  // Commit products and verify the sale outbox in the same transaction. IndexedDB serializes
  // this transaction against createOfflineSale(), preventing a refresh from restoring stock
  // that was already reserved locally after the network went down.
  const tx = db.transaction([STORE_PRODUCTS, STORE_OUTBOX, STORE_PRODUCT_STAGE], 'readwrite');
  const outboxRows = (await requestToPromise(
    tx.objectStore(STORE_OUTBOX).index('companyPos').getAll([companyTaxId, pointOfSaleId])
  )) as OfflineSaleRecord[];
  const hasPendingSales = outboxRows.some((row) =>
    ['pending', 'failed', 'conflict', 'syncing'].includes(row.status)
  );
  if (hasPendingSales) {
    tx.abort();
    throw new Error('Hay ventas offline pendientes; se pospone la actualizacion local de stock.');
  }

  const productsStore = tx.objectStore(STORE_PRODUCTS);
  const existingKeys = await requestToPromise(
    productsStore.index('companyPos').getAllKeys([companyTaxId, pointOfSaleId])
  );
  existingKeys.forEach((key) => productsStore.delete(key));

  const cachedAt = new Date().toISOString();
  staged.forEach(({ _syncId, ...row }) => {
    void _syncId;
    productsStore.put({ ...row, _cachedAt: cachedAt });
    tx.objectStore(STORE_PRODUCT_STAGE).delete(row._offlineKey);
  });

  await transactionDone(tx);
  return staged.length;
};

export const searchOfflineProducts = async (
  companyTaxId: string,
  pointOfSaleId: string,
  search: string,
  options?: OfflineProductFilterOptions
): Promise<IProduct[]> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_PRODUCTS, 'readonly');
  const rows = (await requestToPromise(
    tx.objectStore(STORE_PRODUCTS).index('companyPos').getAll([companyTaxId, pointOfSaleId])
  )) as OfflineProductRecord[];
  await transactionDone(tx);

  return filterOfflineProducts(rows, search, options).map((row) => {
    const product = { ...row };
    Reflect.deleteProperty(product, '_offlineKey');
    Reflect.deleteProperty(product, '_companyTaxId');
    Reflect.deleteProperty(product, '_pointOfSaleId');
    Reflect.deleteProperty(product, '_cachedAt');
    return product;
  });
};

export const getOfflineProduct = async (
  product: Pick<IProduct, 'code' | 'vin' | 'marketCode' | 'warehouseCode'>,
  pointOfSaleId: string
): Promise<IProduct | undefined> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_PRODUCTS, 'readonly');
  const row = (await requestToPromise(
    tx.objectStore(STORE_PRODUCTS).get(buildOfflineProductKey(product, pointOfSaleId))
  )) as OfflineProductRecord | undefined;
  await transactionDone(tx);
  if (!row) return undefined;
  const result = { ...row };
  Reflect.deleteProperty(result, '_offlineKey');
  Reflect.deleteProperty(result, '_companyTaxId');
  Reflect.deleteProperty(result, '_pointOfSaleId');
  Reflect.deleteProperty(result, '_cachedAt');
  return result;
};

export const setOfflineCache = async <T>(key: string, value: T): Promise<void> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_CACHE, 'readwrite');
  tx.objectStore(STORE_CACHE).put({
    key,
    value,
    updatedAt: new Date().toISOString(),
  } satisfies OfflineCacheRecord<T>);
  await transactionDone(tx);
};

export const getOfflineCache = async <T>(key: string): Promise<T | undefined> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_CACHE, 'readonly');
  const row = (await requestToPromise(tx.objectStore(STORE_CACHE).get(key))) as
    | OfflineCacheRecord<T>
    | undefined;
  await transactionDone(tx);
  return row?.value;
};

export const mergeCorrelativesForPointOfSale = async (
  correlatives: OfflineCorrelative[],
  pointOfSale: IPointOfSale
): Promise<void> => {
  const supplierId = pointOfSale.companyTaxId;
  const serialKeys = new Set(
    pointOfSale.series.map(
      (serial) => `${serial.receiptTypeId?.toUpperCase() ?? ''}|${serial.code?.toUpperCase() ?? ''}`
    )
  );

  const incoming = correlatives.filter(
    (item) =>
      item.supplierId === supplierId &&
      serialKeys.has(
        `${item.receiptTypeId?.toUpperCase() ?? ''}|${item.serieCode?.toUpperCase() ?? ''}`
      )
  );

  if (!incoming.length) return;

  const db = await openOfflineDb();
  const tx = db.transaction(STORE_CORRELATIVES, 'readwrite');
  const store = tx.objectStore(STORE_CORRELATIVES);
  const existing = (await requestToPromise(
    store.index('supplier').getAll(supplierId)
  )) as OfflineCorrelative[];

  const byBusinessKey = new Map(
    existing.map((row) => [
      `${row.receiptTypeId?.toUpperCase() ?? ''}|${row.serieCode?.toUpperCase() ?? ''}`,
      row,
    ])
  );

  for (const serverRow of incoming) {
    const businessKey = `${serverRow.receiptTypeId?.toUpperCase() ?? ''}|${serverRow.serieCode?.toUpperCase() ?? ''}`;
    const localRow =
      existing.find((row) => row.id === serverRow.id) ?? byBusinessKey.get(businessKey);

    // Nunca se retrocede un correlativo local. Esto es importante cuando otro PV de la misma
    // sociedad se actualiza mientras existe una venta offline aún no reflejada por el servidor.
    const nextValue = Math.max(Number(localRow?.value ?? 0), Number(serverRow.value ?? 0));

    if (localRow && localRow.id !== serverRow.id) {
      store.delete(localRow.id);
    }

    const merged = { ...serverRow, value: nextValue };
    store.put(merged);
    byBusinessKey.set(businessKey, merged);
  }

  await transactionDone(tx);
};

const uuid = () => crypto.randomUUID();

const reserveStockInTransaction = async (
  store: IDBObjectStore,
  payload: CreateSaleMovementRequest,
  pointOfSaleId: string
) => {
  for (const line of payload.lines ?? []) {
    if (line.material.category === MaterialCategory.Service) continue;
    // El payload de venta reduce material y ya no conserva marketCode; buscamos por POS + código/VIN + almacén.
    const all = (await requestToPromise(store.getAll())) as OfflineProductRecord[];
    const row = all.find(
      (candidate) =>
        candidate._pointOfSaleId === pointOfSaleId &&
        (candidate.vin === line.material.vin || candidate.code === line.material.code) &&
        candidate.warehouseCode === line.material.warehouseCode
    );
    if (!row) {
      throw new Error(
        `No existe stock offline para ${line.material.vin || line.material.code}. Sincronice antes de vender.`
      );
    }
    const available = Number(row.stock ?? 0);
    const requested = Number(line.quantity ?? 0);
    if (available < requested) {
      throw new Error(
        `${row.description || row.code} tiene stock offline ${available}; se intentó vender ${requested}.`
      );
    }
    row.stock = available - requested;
    store.put(row);
  }
};

export const createOfflineSale = async (
  payload: CreateSaleMovementRequest,
  selectedPos: IPointOfSale,
  idempotencyKey: string
): Promise<OfflineSaleRecord> => {
  const db = await openOfflineDb();
  const tx = db.transaction([STORE_PRODUCTS, STORE_CORRELATIVES, STORE_OUTBOX], 'readwrite');
  const productStore = tx.objectStore(STORE_PRODUCTS);
  const correlativesStore = tx.objectStore(STORE_CORRELATIVES);
  const outboxStore = tx.objectStore(STORE_OUTBOX);

  const serial = selectedPos.series.find((x) => x.id === payload.serialId && x.enabled !== false);
  if (!serial?.id)
    throw new Error('No hay una serie habilitada para emitir el comprobante offline.');

  const supplierCorrelatives = (await requestToPromise(
    correlativesStore.index('supplier').getAll(selectedPos.companyTaxId)
  )) as OfflineCorrelative[];
  const correlative = supplierCorrelatives.find(
    (x) =>
      x.serieCode === serial.code &&
      x.receiptTypeId?.toUpperCase() === serial.receiptTypeId?.toUpperCase()
  );

  if (!correlative) {
    throw new Error(
      `No existe un correlativo sincronizado para la serie ${serial.code}. Conéctese y sincronice antes de vender offline.`
    );
  }

  const nextCorrelative = Number(correlative.value ?? 0) + 1;
  correlative.value = nextCorrelative;
  correlativesStore.put(correlative);

  await reserveStockInTransaction(productStore, payload, selectedPos.id);

  const now = new Date().toISOString();
  const receiptPrefix =
    serial.receiptTypeId.toUpperCase() === Constants.INVOICE_TYPE_ID.toUpperCase()
      ? 'F'
      : serial.receiptTypeId.toUpperCase() === Constants.SALE_TICKET_ID.toUpperCase()
        ? 'B'
        : '';
  const serieCode =
    receiptPrefix && !serial.code.toUpperCase().startsWith(receiptPrefix)
      ? `${receiptPrefix}${serial.code}`
      : serial.code;

  const record: OfflineSaleRecord = {
    id: uuid(),
    idempotencyKey,
    companyTaxId: selectedPos.companyTaxId,
    pointOfSaleId: selectedPos.id,
    serialId: serial.id,
    serieCode: serial.code,
    receiptTypeId: serial.receiptTypeId,
    invoiceCorrelative: nextCorrelative,
    receiptCode: `${serieCode}-${String(nextCorrelative).padStart(8, '0')}`,
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    status: 'pending',
    payload,
  };
  outboxStore.add(record);
  await transactionDone(tx);
  window.dispatchEvent(new CustomEvent('epos:offline-sales-changed'));
  return record;
};

export const listOfflineSales = async (
  statuses?: OfflineSaleStatus[]
): Promise<OfflineSaleRecord[]> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_OUTBOX, 'readonly');
  const rows = (await requestToPromise(
    tx.objectStore(STORE_OUTBOX).getAll()
  )) as OfflineSaleRecord[];
  await transactionDone(tx);
  return rows
    .filter((row) => !statuses?.length || statuses.includes(row.status))
    .sort(
      (a, b) =>
        a.invoiceCorrelative - b.invoiceCorrelative || a.createdAt.localeCompare(b.createdAt)
    );
};

export const updateOfflineSale = async (
  id: string,
  patch: Partial<OfflineSaleRecord>
): Promise<void> => {
  const db = await openOfflineDb();
  const tx = db.transaction(STORE_OUTBOX, 'readwrite');
  const store = tx.objectStore(STORE_OUTBOX);
  const current = (await requestToPromise(store.get(id))) as OfflineSaleRecord | undefined;
  if (current) store.put({ ...current, ...patch, updatedAt: new Date().toISOString() });
  await transactionDone(tx);
  window.dispatchEvent(new CustomEvent('epos:offline-sales-changed'));
};

export const getOfflineReadiness = async (
  selectedPos: IPointOfSale | null
): Promise<OfflineReadiness> => {
  if (!selectedPos?.isOffline || selectedPos.isDealer) {
    return {
      enabled: false,
      ready: false,
      productCount: 0,
      correlativeCount: 0,
      pendingSales: 0,
      reason: selectedPos?.isDealer
        ? 'La funcionalidad offline no aplica a dealers.'
        : 'Sociedad sin soporte offline.',
    };
  }
  const db = await openOfflineDb();
  const tx = db.transaction(
    [STORE_PRODUCTS, STORE_CORRELATIVES, STORE_CACHE, STORE_OUTBOX],
    'readonly'
  );
  const productCount = await requestToPromise(
    tx
      .objectStore(STORE_PRODUCTS)
      .index('companyPos')
      .count([selectedPos.companyTaxId, selectedPos.id])
  );
  const correlatives = (await requestToPromise(
    tx.objectStore(STORE_CORRELATIVES).index('supplier').getAll(selectedPos.companyTaxId)
  )) as OfflineCorrelative[];
  const meta = (await requestToPromise(
    tx.objectStore(STORE_CACHE).get(`sync:${selectedPos.id}`)
  )) as OfflineCacheRecord<{ lastSyncAt: string }> | undefined;
  const outbox = (await requestToPromise(
    tx.objectStore(STORE_OUTBOX).getAll()
  )) as OfflineSaleRecord[];
  await transactionDone(tx);
  const pendingSales = outbox.filter((x) =>
    ['pending', 'failed', 'conflict', 'syncing'].includes(x.status)
  ).length;
  const saleSerials = selectedPos.series.filter(
    (serial) =>
      serial.enabled !== false &&
      [Constants.INVOICE_TYPE_ID, Constants.SALE_TICKET_ID].some(
        (id) => id.toUpperCase() === serial.receiptTypeId?.toUpperCase()
      )
  );
  const hasRequiredCorrelatives =
    saleSerials.length > 0 &&
    saleSerials.every((serial) =>
      correlatives.some(
        (row) =>
          row.serieCode === serial.code &&
          row.receiptTypeId?.toUpperCase() === serial.receiptTypeId?.toUpperCase()
      )
    );
  const ready = productCount > 0 && hasRequiredCorrelatives && !!meta?.value?.lastSyncAt;
  return {
    enabled: true,
    ready,
    lastSyncAt: meta?.value?.lastSyncAt,
    productCount,
    correlativeCount: correlatives.length,
    pendingSales,
    reason: ready
      ? undefined
      : 'Falta sincronizar stock y/o correlativos de factura/boleta para operar sin Internet.',
  };
};
